import { ComponentPropsWithoutRef, type JSX } from 'react'
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, Text, forwardRef } from '../../chakra'
import { ChevronRight } from '../../../icons'
import { asLink } from '../links/Links'

export type BreadcrumbLinkAttrs = {
  /** The text to display for the link. */
  name: string | JSX.Element
  /** The location to link to, if applicable. */
  href?: string
  /** The location to link to within ReachRouter, if applicable. */
  to?: string
  as?: any
  onClick?: () => void
  'data-testid'?: string
}

/**
 * I (Bruce) ripped reach router out of this component but left the link.to stuff
 *  because we still have a few breadcrumbs that use it. We should probably change those
 *  in some future PR.
 */
export type BreadcrumbsProps = {
  /** An array of links to be rendered as the breadcrumb. */
  links: BreadcrumbLinkAttrs[]
} & ComponentPropsWithoutRef<typeof Breadcrumb>

/**
 * Displays a list of links as breadcrumbs.
 *
 * Uses Chakra's `Breadcrumb` and related components.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-breadcrumbs--docs
 */
export const Breadcrumbs = forwardRef(({ links, ...rest }: BreadcrumbsProps, ref) => (
  <Breadcrumb
    spacing="1"
    listProps={{ flexWrap: { base: 'wrap', sm: 'nowrap' } }}
    separator={<ChevronRight color="gray.400" w="16px" h="16px" />}
    ref={ref}
    {...rest}>
    {links.map((link, i) => {
      const active = i === links.length - 1
      return (
        <BreadcrumbItem key={link.href || link.to || i} isCurrentPage={active} maxW={[320, 500]} whiteSpace="nowrap">
          {active ? (
            <Text as="span" textStyle="bodySemi" textOverflow="ellipsis" overflow="hidden" data-testid="current-page">
              {link.name}
            </Text>
          ) : (
            <BreadcrumbLink
              {...asLink(link.href || link.to)}
              ref={ref}
              textStyle="link"
              textOverflow="ellipsis"
              overflow="hidden"
              onClick={link.onClick}
              data-testid={link['data-testid']}>
              {link.name}
            </BreadcrumbLink>
          )}
        </BreadcrumbItem>
      )
    })}
  </Breadcrumb>
))

Breadcrumbs.displayName = 'Breadcrumbs'
